import { useEffect } from "react";
import { catchError, tap, throwError, type Observable } from "rxjs";

import { type APILibraryCategoryRequestType } from "@/core/data_sources/models/APILibraryCategory";
import { Store } from "@/core/data_sources/store";
import { AppStore } from "@/core/data_sources/store/app";
import { useService } from "@/core/utils/hooks";
import { requestToObservable } from "@/core/utils/observables";

import {
  deleteCategory,
  getCategories,
  patchCategory,
  postCategory,
} from "../services";
import { LibraryCategoriesState } from "./state";

import type { LibraryCategoriesActions } from "./actions";
import type { LibraryCategoriesHooks } from "./hooks";

export class LibraryCategoriesStore extends Store<
  LibraryCategoriesState,
  LibraryCategoriesActions,
  LibraryCategoriesHooks
> {
  private static _instance: LibraryCategoriesStore;

  static get instance() {
    LibraryCategoriesStore._instance =
      LibraryCategoriesStore._instance || new LibraryCategoriesStore();
    return this._instance;
  }
  constructor() {
    super(
      new LibraryCategoriesState(),
      {
        createCategory: (
          category: APILibraryCategoryRequestType,
        ): Observable<unknown> => {
          AppStore.instance.actions.isLoading(true);
          return requestToObservable(() => postCategory(category)).pipe(
            tap(() => AppStore.instance.actions.isLoading(false)),
            catchError((error) => {
              AppStore.instance.actions.isLoading(false);
              return throwError(() => error);
            }),
          );
        },
        updateCategory: (
          id: string,
          category: APILibraryCategoryRequestType,
        ): Observable<unknown> => {
          AppStore.instance.actions.isLoading(true);
          return requestToObservable(() =>
            patchCategory(Number(id), category),
          ).pipe(
            tap(() => AppStore.instance.actions.isLoading(false)),
            catchError((error) => {
              AppStore.instance.actions.isLoading(false);
              return throwError(() => error);
            }),
          );
        },
        deleteCategory: (id: string): Observable<unknown> => {
          AppStore.instance.actions.isLoading(true);
          return requestToObservable(() => deleteCategory(Number(id))).pipe(
            tap(() => AppStore.instance.actions.isLoading(false)),
            catchError((error) => {
              AppStore.instance.actions.isLoading(false);
              return throwError(() => error);
            }),
          );
        },
      },
      {
        useCategoryStoreService: (q) => {
          const { data, error, loading, query, setQuery } = useService(
            getCategories,
            q,
          );
          useEffect(() => {
            if (error) {
              return;
            }
            if (!loading && data) {
              this.state.setCategories(data?.data ?? []);
            }
          }, [loading, data, error]);
          return { data, error, loading, query, setQuery };
        },
      },
    );
  }
}
